// First-time setup: a full-page onboarding shown once per clinician + org, to a clinician whose setup
// flag for that pair is not set yet. "See my plan" commits the unit scope and time budget.
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked
} from '@angular/core'
import {
  animate,
  query,
  stagger,
  style,
  transition,
  trigger
} from '@angular/animations'
import { NavigationEnd, Router } from '@angular/router'
import { Store } from '@ngxs/store'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { filter, map, take } from 'rxjs'
import { RouteName } from '../../../core/enums/route-name.enum'
import { UserState } from '../../../store/user/user.state'
import { AuthState } from '../../../store/auth/auth.state'
import { PaRoleResolverService } from '../data/pa-role-resolver.service'
import { OrgScopeService } from '../org-scope/org-scope.service'
import { TimeModeService } from '../time-mode/time-mode.service'
import { PaSessionService } from '../session/pa-session.service'
import { PaShellReadyService } from '../session/pa-shell-ready.service'
import { isMyPlanUrl } from '../session/my-plan-url'
import { TimeModeValue } from '../ui/pa-ui.types'
import { PaUnitMultiselectComponent } from '../ui/pa-unit-multiselect/pa-unit-multiselect.component'
import { PaTimePickerComponent } from '../ui/pa-time-picker/pa-time-picker.component'
import { PaOrgPickerComponent } from '../org-scope/pa-org-picker.component'
import { PREFERS_REDUCED_MOTION } from '../ui/pa-reduced-motion'
import { isSetupDone, markSetupDone } from './pa-setup-done.storage'

@Component({
  selector: 'aiomed-pa-onboarding',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PaUnitMultiselectComponent,
    PaTimePickerComponent,
    PaOrgPickerComponent
  ],
  // The stagger is compressed so all ~6 children finish inside the 250ms PA transition budget: the last
  // child starts at 5 x 26ms and runs 110ms, 240ms in total.
  animations: [
    trigger('pageFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 }))
      ])
    ]),
    trigger('stepStagger', [
      transition(':enter', [
        query(
          ':scope > *',
          [
            style({ opacity: 0, transform: 'translateY(10px)' }),
            stagger(26, [
              animate(
                '110ms cubic-bezier(0.16, 1, 0.3, 1)',
                style({ opacity: 1, transform: 'none' })
              )
            ])
          ],
          { optional: true }
        )
      ])
    ])
  ],
  templateUrl: './pa-onboarding.component.html',
  styleUrls: ['./pa-onboarding.component.scss']
})
export class PaOnboardingComponent {
  private readonly roleResolver = inject(PaRoleResolverService)
  private readonly orgScope = inject(OrgScopeService)
  private readonly timeMode = inject(TimeModeService)
  private readonly session = inject(PaSessionService)
  private readonly shellReady = inject(PaShellReadyService)
  private readonly store = inject(Store)
  private readonly router = inject(Router)

  // Reduced motion disables the page reveal and card stagger.
  protected readonly reduceMotion = PREFERS_REDUCED_MOTION
  protected readonly visible = signal(false)
  // Wait for the app shell, so the overlay never covers the bare initial loader and the sidebar shows beside it.
  protected readonly show = computed(
    () => this.visible() && this.shellReady.ready()
  )
  // Set once the open onboarding has reached /my-plan. Only a navigation after that is the user leaving.
  private onPlan = false
  /** Working copies, committed to the services only on "See my plan". */
  protected readonly selectedUnits = signal<string[]>([])
  protected readonly time = signal<TimeModeValue>(this.timeMode.value())

  // The service's shared units signal: one subscription to DepartmentState for every surface.
  protected readonly units = this.orgScope.units
  protected readonly orgName = computed(() => this.orgScope.activeOrgName())
  // Reactive so the greeting fills in even when the user store populates after first paint.
  protected readonly firstName = toSignal(
    this.store
      .select(UserState.currentUser)
      .pipe(
        map(
          user =>
            (user as { name?: { firstName?: string } } | null)?.name
              ?.firstName ?? ''
        )
      ),
    { initialValue: '' }
  )

  // The org already decided for (shown or skipped), so the effect does not re-decide on every tick.
  // Keyed by org because setup state is per org: the briefing defers to onboarding when setup is not
  // done, so a per-session decision left a first switch into a new org with neither interstitial. The
  // other half of the key, the clinician, needs no entry here: logout re-arms this to null below.
  private decidedForOrg: string | null = null
  private readonly authed = toSignal(
    this.store.select(AuthState.isAuthenticated),
    { initialValue: false }
  )
  // Null until getUserSelf() resolves after login. The admin check depends on it, so the decision waits
  // for it rather than onboarding an admin misread as a clinician.
  private readonly userType = toSignal(
    this.store
      .select(UserState.currentUser)
      .pipe(
        map(user => (user as { _userType?: string } | null)?._userType ?? null)
      ),
    { initialValue: null }
  )
  // Part of the setup-done key, so a second clinician on the same workstation still gets first-run
  // setup. Lands with `_userType` (one getUserSelf() response), so it needs no separate wait.
  private readonly userId = toSignal(
    this.store.select(UserState.currentUser).pipe(
      map(user => {
        const u = user as { id?: string; _id?: string } | null
        return u?.id ?? u?._id ?? null
      })
    ),
    { initialValue: null as string | null }
  )

  constructor() {
    // The overlay scrolls on its own, so lock the page behind it while open; otherwise platforms with
    // persistent scrollbars paint two bars side by side.
    effect(() => {
      const open = this.show()
      try {
        // Both elements: locking body alone leaves the root element scrollable.
        document.documentElement.style.overflow = open ? 'hidden' : ''
        document.body.style.overflow = open ? 'hidden' : ''
      } catch {
        /* SSR / no document - nothing to lock. */
      }
    })
    inject(DestroyRef).onDestroy(() => {
      try {
        document.documentElement.style.overflow = ''
        document.body.style.overflow = ''
      } catch {
        /* no document */
      }
    })
    // Decide once auth, the org id and the profile have all resolved. Deciding on the bare auth event
    // read the setup flag before the org was known, so a completed setup re-showed on every load.
    effect(() => {
      const isAuthed = this.authed()
      const orgId = this.orgScope.activeOrgId() // tracked: re-runs when the org resolves
      const userType = this.userType() // tracked: re-runs when the profile loads
      const userId = this.userId() // tracked: the flag is per clinician, not per browser
      if (!isAuthed) {
        this.visible.set(false)
        this.decidedForOrg = null // re-arm for the next login
        return
      }
      if (this.decidedForOrg === orgId || orgId == null || userType == null) {
        return
      }
      // No separate isPaEnabled() check: isAssistantVisible in tryShow folds the whole feature gate.
      if (isSetupDone(userId, orgId)) {
        this.decidedForOrg = orgId
        return
      }
      this.decidedForOrg = orgId
      this.tryShow()
    })

    // A Step-1 org switch resets the working copy to "All units", so the old org's unit ids can never be
    // committed as the new org's scope. `visible` is read untracked so later service-side changes never
    // clobber in-progress edits.
    effect(() => {
      this.orgScope.activeOrgId() // tracked: the org switch
      untracked(() => {
        if (this.visible()) {
          this.selectedUnits.set([])
        }
      })
    })

    // The sidebar stays clickable beside the overlay. Leaving /my-plan from it (or with Back) would load
    // the new page under the onboarding, so close it there as "Skip for now" does.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(e => {
        if (!this.visible()) {
          return
        }
        if (isMyPlanUrl(e.urlAfterRedirects)) {
          this.onPlan = true
        } else if (this.onPlan) {
          this.dismiss()
        }
      })
  }

  /** Resolve the role, then show. Auth and this clinician's setup flag were already checked by the caller. */
  private tryShow(): void {
    this.roleResolver
      .resolve$()
      .pipe(take(1))
      .subscribe(resolution => {
        if (!resolution.isAssistantVisible) {
          return // admin, flag-off env or non-allowlisted user: never onboard
        }
        // Onboarding supersedes the briefing on this first-run login.
        this.session.markBriefingShown()
        // First run always opens org-wide ([] = "All units"), whatever department the platform restored;
        // returning users never reach this screen, so their saved scope is untouched. Committing [] writes
        // through to DepartmentState as every unit, so the platform filter stays in sync.
        this.selectedUnits.set([])
        this.time.set(this.timeMode.value())
        // Already on /my-plan, the navigate below is a same-URL no-op that emits no NavigationEnd.
        this.onPlan = isMyPlanUrl(this.router.url)
        this.visible.set(true)
        // Overlay /my-plan so the sidebar's Home entry is active behind it.
        this.router.navigate(['/' + RouteName.MyPlan])
      })
  }

  protected onUnitsChange(unitIds: string[]): void {
    this.selectedUnits.set(unitIds)
  }

  protected onTimeChange(value: TimeModeValue): void {
    this.time.set(value)
  }

  protected seeMyPlan(): void {
    this.orgScope.selectUnits(this.selectedUnits())
    this.timeMode.set(this.time())
    this.finish()
  }

  protected skip(): void {
    this.dismiss()
    this.router.navigate(['/' + RouteName.Patients])
  }

  // Skip scopes org-wide and leaves the assistant flow; setup is still marked done so it does not
  // re-prompt. Leaving from the sidebar does the same, minus the navigation the user already made.
  private dismiss(): void {
    this.orgScope.selectUnits([])
    markSetupDone(this.userId(), this.orgScope.activeOrgId())
    this.visible.set(false)
  }

  private finish(): void {
    markSetupDone(this.userId(), this.orgScope.activeOrgId())
    this.visible.set(false)
    this.router.navigate(['/' + RouteName.MyPlan])
  }
}
